import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { LogOut, IdCard } from "lucide-react";

export default function Header() {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", brokerage_name: "", dre_license: "" });

  const openProfile = () => {
    setForm({ name: user?.name || "", brokerage_name: user?.brokerage_name || "", dre_license: user?.dre_license || "" });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success("Profile saved");
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5">
          <img src="/equitas-logo.png" alt="Equitas" className="w-8 h-8 rounded-full" />
          <span className="font-heading text-xl text-primary">Unified Transaction Hub</span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <button data-testid="profile-button" onClick={openProfile}
            className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <IdCard className="w-4 h-4" /> {user?.email}
          </button>
          <Button variant="ghost" size="sm" data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}
            className="text-muted-foreground hover:text-primary gap-2">
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card" data-testid="profile-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-primary">Broker profile</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground">
            Your brokerage name and DRE license auto-fill new commission demands. You can still edit them per demand.
          </DialogDescription>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Full name</Label>
              <Input data-testid="profile-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Louis Fong" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Brokerage name</Label>
              <Input data-testid="profile-brokerage" className="mt-1.5" value={form.brokerage_name} onChange={(e) => setForm({ ...form, brokerage_name: e.target.value })} placeholder="Equitas Residential" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">DRE license #</Label>
              <Input data-testid="profile-dre" className="mt-1.5" value={form.dre_license} onChange={(e) => setForm({ ...form, dre_license: e.target.value })} placeholder="01234567" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="profile-save" disabled={saving} onClick={save} className="bg-primary text-primary-foreground rounded-full">
              {saving ? "Saving…" : "Save profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
